import { Flatfile } from '@flatfile/api';

/** Flatfile column key is `fieldKey`; API payloads need `customFieldConfigId`. */
export type FormCustomFieldColumn = {
  /** Config UUID; required for persisting CSV values via createInteraction (same shape as single invite). */
  fieldKey: string;
  fieldName: string;
  customFieldConfigId: string;
  fieldType?: string;
  options?: string[];
};

export const BASE_FIELD_KEYS = new Set([
  'email',
  'firstName',
  'lastName',
  'countryCode',
  'cellNumber',
  'company',
]);

const BASE_FIELDS: Flatfile.Property[] = [
  {
    key: 'email',
    type: 'string',
    label: 'Visitor Email',
    constraints: [{ type: 'unique' }],
  },
  {
    key: 'firstName',
    type: 'string',
    label: 'Visitor First Name',
    constraints: [{ type: 'required' }],
  },
  {
    key: 'lastName',
    type: 'string',
    label: 'Visitor Last Name',
    constraints: [{ type: 'required' }],
  },
  {
    key: 'countryCode',
    type: 'string',
    label: 'Visitor Country Code',
  },
  {
    key: 'cellNumber',
    type: 'string',
    label: 'Visitor Cell Number',
  },
  {
    key: 'company',
    type: 'string',
    label: 'Visitor Company',
  },
];

/**
 * Builds Flatfile workbook config with base visitor fields plus form custom fields.
 * Custom fields added to the registration form appear as required columns so uploads align with form config.
 * Custom fields whose `fieldKey` matches a base column key are omitted to avoid duplicate Flatfile keys and
 * misrouting in `processVisitorData`.
 */
export function buildWorkbookConfig(
  formCustomFields: FormCustomFieldColumn[] = []
): Flatfile.CreateWorkbookConfig {
  const customFieldProperties: Flatfile.Property[] = formCustomFields
    .filter(({ fieldKey }) => !BASE_FIELD_KEYS.has(fieldKey))
    .map(({ fieldKey, fieldName }) => ({
      key: fieldKey,
      type: 'string' as const,
      label: fieldName,
      constraints: [{ type: 'required' as const }],
    }));

  return {
    name: 'Import Visitors',
    labels: ['pinned'],
    sheets: [
      {
        name: 'Visitors',
        slug: 'visitors',
        fields: [...BASE_FIELDS, ...customFieldProperties],
      },
    ],
    actions: [
      {
        operation: 'submitActionFg',
        mode: 'foreground',
        label: 'Submit',
        description: 'Submit data',
        primary: true,
        constraints: [{ type: 'hasAllValid' }, { type: 'hasData' }],
      },
    ],
  };
}
